import { useEffect, useCallback } from "react";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import Button from "react-bootstrap/Button";
import "./ProjectModal.css";

export const ProjectModal = ({
  isOpen,
  onClose,
  title,
  description,
  overview,
  highlights,
  techStack = [],
  images = [],
  demo,
  code,
}) => {
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className="pmodal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${title}`}
    >
      <div
        className="pmodal-panel"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="pmodal-header">
          <h4 className="pmodal-title">{title}</h4>
          <button
            className="pmodal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            &times;
          </button>
        </div>

        {/* Scrollable body */}
        <div className="pmodal-body">
          {/* Carousel */}
          <div className="pmodal-carousel">
            <Carousel
              autoPlay
              infiniteLoop
              interval={3500}
              showThumbs={false}
              showStatus={false}
              swipeable
              emulateTouch
              stopOnHover
            >
              {images.map((image, index) => (
                <div className="pmodal-slide" key={`modal-slide-${index}`}>
                  <img src={image} alt={`${title} screenshot ${index + 1}`} />
                </div>
              ))}
            </Carousel>
          </div>

          {/* Project title */}
          <h3 className="pmodal-project-title">{title}</h3>

          {/* Tech chips */}
          <div className="pmodal-chips" aria-label={`${title} tech stack`}>
            {techStack.map((tech) => (
              <span className="pmodal-chip" key={`modal-chip-${tech}`}>
                {tech}
              </span>
            ))}
          </div>

          {/* Full description or Structured Sections */}
          <div className="pmodal-description">
            {overview && (
              <div className="pmodal-section">
                <h5 className="pmodal-section-title">Overview</h5>
                <p className="pmodal-overview-text">{overview}</p>
              </div>
            )}

            {highlights && highlights.length > 0 && (
              <div className="pmodal-section">
                <h5 className="pmodal-section-title">Key Highlights</h5>
                <ul className="pmodal-highlights-list">
                  {highlights.map((item, index) => {
                    const isObj = typeof item === "object" && item !== null;
                    const label = isObj ? item.label : null;
                    const text = isObj ? item.text : item;

                    return (
                      <li key={`highlight-${index}`} className="pmodal-highlight-item">
                        {label && (
                          <span className="pmodal-highlight-label">{label}: </span>
                        )}
                        <span className="pmodal-highlight-text">{text}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {!overview && !highlights && (
              <div className="pmodal-legacy-desc">{description}</div>
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div className="pmodal-footer">
          {demo && (
            <Button
              href={demo}
              target="_blank"
              rel="noreferrer"
              variant="primary"
              className="pmodal-btn"
            >
              Project Demo
            </Button>
          )}
          {code && (
            <Button
              href={code}
              target="_blank"
              rel="noreferrer"
              variant="outline-warning"
              className="pmodal-btn"
            >
              View Code
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
